// src/oura/components/BusinessSpendCard.tsx
//
// What we have spent on one client (#4816, BE ourteam-backend#6689), on
// History, for staff. All time per source, this week against last week, and a
// bar per week (Monday to Sunday, Malaysia time) stacked by source.
//
// "Recorded spend", never "total cost": several paid steps record no cost yet,
// and the card lists them under the chart rather than letting the figure read
// as the whole bill. A row with no recorded figure is never a $0 (spend.ts).
//
// The chart is a row of plain columns: a dozen bars need no charting library,
// and HTML keeps each bar's figure crisp where a stretched SVG would warp it.

import { useEffect, useState } from "react";
import { getBusinessSpend, type AeoBusinessSpend } from "@service/aeo";
import type { AeoSpendSource, AeoSpendWeek } from "@service/aeo/types";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { cn } from "../lib/cn";
import { formatUsd } from "../lib/spend";
import { shortDateOrNull, weekRange } from "../lib/oura";

type SourceKey = AeoSpendSource["key"];

/** One colour per source, stacked in this order from the bottom. */
const SOURCE_STYLE: Record<SourceKey, { dot: string }> = {
    scans: { dot: "bg-blue-500" },
    skillRuns: { dot: "bg-amber-400" },
    docSummaries: { dot: "bg-violet-400" },
};
const STACK: SourceKey[] = ["scans", "skillRuns", "docSummaries"];

/** "+$4.60 (+174%)", "−$1.20", "no change". Null when there is no week before to compare with. */
export function weekChange(thisWeek: number, lastWeek: number | null): string | null {
    if (lastWeek === null) return null;
    const diff = thisWeek - lastWeek;
    if (Math.abs(diff) < 0.005) return "no change";
    const sign = diff > 0 ? "+" : "−";
    const pct = lastWeek > 0 ? ` (${sign}${Math.round((Math.abs(diff) / lastWeek) * 100)}%)` : "";
    return `${sign}${formatUsd(Math.abs(diff))}${pct}`;
}

/** "26 Oct 2026". The Monday is a calendar date, not a moment: built as local midnight here, so it prints as
 *  itself in any time zone (checked in America/Los_Angeles, UTC, Asia/Kuala_Lumpur and UTC+14), whatever
 *  rule a shared date helper uses. */
function weekLabel(start: string): string {
    const local = /^\d{4}-\d{2}-\d{2}$/.test(start) ? `${start}T00:00:00` : start;
    return shortDateOrNull(local) ?? start;
}

/** A bar's figure, short enough to sit above it: "$7", "$0.42". */
function barFigure(usd: number): string {
    return usd >= 10 ? `$${Math.round(usd)}` : formatUsd(usd);
}

function SpendChart({ weeks, picked, onPick }: { weeks: AeoSpendWeek[]; picked: number; onPick: (i: number) => void }) {
    const top = Math.max(...weeks.map((w) => w.totalUsd), 0.01);
    return (
        <div>
            <div className="flex h-44 items-end gap-1 border-b border-gray-200" role="group" aria-label="Recorded spend per week, stacked by source">
                {weeks.map((w, i) => (
                    <button
                        key={w.start}
                        type="button"
                        onMouseEnter={() => onPick(i)}
                        onFocus={() => onPick(i)}
                        onClick={() => onPick(i)}
                        aria-pressed={i === picked}
                        aria-label={`Week of ${weekLabel(w.start)}: ${formatUsd(w.totalUsd)}`}
                        className={cn("flex h-full min-w-0 flex-1 flex-col items-center justify-end rounded-t-md px-0.5 pt-1 outline-none",
                            "focus-visible:ring-2 focus-visible:ring-blue-500", i === picked ? "bg-gray-100" : "hover:bg-gray-50")}
                    >
                        {w.totalUsd > 0 && (
                            <span className="mb-0.5 text-[10px] leading-none tabular-nums text-gray-500">{barFigure(w.totalUsd)}</span>
                        )}
                        {/* The bar, stacked from the bottom: scans, then doc drafts, then doc summaries. */}
                        <span className="flex w-full max-w-[28px] flex-col-reverse overflow-hidden rounded-t-[3px]"
                            style={{ height: `${(w.totalUsd / top) * 85}%` }}>
                            {STACK.map((key) => {
                                const usd = w.bySource[key] ?? 0;
                                return usd > 0
                                    ? <span key={key} className={cn("block w-full", SOURCE_STYLE[key].dot)} style={{ height: `${(usd / w.totalUsd) * 100}%` }} />
                                    : null;
                            })}
                        </span>
                    </button>
                ))}
            </div>
            <div className="mt-1 flex justify-between text-xs text-gray-400">
                <span>{weekLabel(weeks[0].start)}</span>
                <span>This week</span>
            </div>
        </div>
    );
}

/** Staff only: the server refuses a client, and the caller renders this for staff alone. */
export function BusinessSpendCard({ businessId }: { businessId?: string }) {
    const [spend, setSpend] = useState<AeoBusinessSpend | null | "loading">("loading");
    const [picked, setPicked] = useState(-1);

    useEffect(() => {
        if (!businessId) { setSpend(null); return; }
        let cancelled = false;
        setSpend("loading");
        void getBusinessSpend(businessId).then((s) => {
            if (cancelled) return;
            setSpend(s);
            setPicked(s ? s.weeks.length - 1 : -1);
        });
        return () => { cancelled = true; };
    }, [businessId]);

    if (spend === "loading") {
        return (
            <Card>
                <CardHeader title="Spend on this client" />
                <CardBody><p className="m-0 text-sm text-gray-500">Loading…</p></CardBody>
            </Card>
        );
    }
    if (spend === null) {
        return (
            <Card>
                <CardHeader title="Spend on this client" />
                <CardBody><p className="m-0 text-sm text-gray-500">The spend could not be read just now.</p></CardBody>
            </Card>
        );
    }

    const change = weekChange(spend.thisWeekUsd, spend.lastWeekUsd);
    const week = spend.weeks[picked] ?? spend.weeks[spend.weeks.length - 1];
    const unknown = spend.sources.reduce((n, s) => n + s.unknown, 0);
    const parts = STACK.map((key) => ({ key, usd: week.bySource[key] ?? 0, label: spend.sources.find((s) => s.key === key)?.label ?? key }))
        .filter((p) => p.usd > 0);

    return (
        <Card>
            <CardHeader
                title="Spend on this client"
                description="Recorded spend, all time and week by week (Monday to Sunday). Some paid steps do not record a cost yet; they are listed below."
            />
            <CardBody className="space-y-4">
                <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                        <dt className="text-xs text-gray-500">All time</dt>
                        <dd className="m-0 text-lg font-semibold tabular-nums text-gray-900">{formatUsd(spend.totalUsd)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs text-gray-500">This week</dt>
                        <dd className="m-0 text-lg font-semibold tabular-nums text-gray-900">
                            {formatUsd(spend.thisWeekUsd)}
                            {change && (
                                <span className={cn("ml-2 text-xs font-medium",
                                    change.startsWith("+") ? "text-rose-600" : change.startsWith("−") ? "text-emerald-600" : "text-gray-400")}>
                                    {change} vs last week
                                </span>
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs text-gray-500">Last week</dt>
                        <dd className="m-0 text-lg font-semibold tabular-nums text-gray-900">
                            {spend.lastWeekUsd === null ? "—" : formatUsd(spend.lastWeekUsd)}
                        </dd>
                    </div>
                </dl>

                <SpendChart weeks={spend.weeks} picked={picked} onPick={setPicked} />

                <p className="m-0 text-sm tabular-nums text-gray-700" aria-live="polite">
                    <span className="font-medium">{weekRange(`${week.start}T00:00:00`)}:</span> {formatUsd(week.totalUsd)}
                    {parts.length > 0 && <span className="text-gray-500"> · {parts.map((p) => `${p.label} ${formatUsd(p.usd)}`).join(" · ")}</span>}
                </p>

                <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1 p-0 text-xs text-gray-600">
                    {STACK.map((key) => {
                        const s = spend.sources.find((x) => x.key === key);
                        if (!s) return null;
                        return (
                            <li key={key} className="flex items-center gap-1.5 tabular-nums">
                                <span aria-hidden className={cn("h-2 w-2 rounded-full", SOURCE_STYLE[key].dot)} />
                                {s.label} {formatUsd(s.totalUsd)}
                                <span className="text-gray-400">
                                    ({s.items}{s.unknown > 0 ? `, ${s.unknown} not recorded` : ""})
                                </span>
                            </li>
                        );
                    })}
                </ul>

                {unknown > 0 && (
                    <p className="m-0 text-xs text-gray-500">
                        {unknown} {unknown === 1 ? "item has" : "items have"} no recorded figure and {unknown === 1 ? "is" : "are"} left out of the totals, not counted as $0.
                    </p>
                )}

                {spend.notCounted.length > 0 && (
                    <details className="text-xs text-gray-500">
                        <summary className="cursor-pointer select-none">Not counted yet ({spend.notCounted.length})</summary>
                        <ul className="mb-0 mt-1.5 space-y-0.5 pl-4">
                            {spend.notCounted.map((n) => <li key={n}>{n}</li>)}
                        </ul>
                    </details>
                )}
            </CardBody>
        </Card>
    );
}
